/**
 * Serviço para integração com Google Calendar
 * Gera URLs para adicionar eventos ao Google Calendar
 * Sincronização bidirecional com Google Calendar API
 */

import { getToken } from './googleCalendarOAuthService';

export interface ConsultationEvent {
  title: string;
  description?: string;
  startDate: Date;
  endDate: Date;
  location?: string;
  googleEventId?: string; // ID do evento no Google Calendar
}

export interface GoogleCalendarEvent {
  id?: string;
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location?: string;
  reminders?: {
    useDefault: boolean;
  };
}

/**
 * Gera URL do Google Calendar para adicionar evento (método manual)
 */
export function generateGoogleCalendarUrl(event: ConsultationEvent): string {
  const formatDate = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatDate(event.startDate)}/${formatDate(event.endDate)}`,
  });

  if (event.description) {
    params.append('details', event.description);
  }

  if (event.location) {
    params.append('location', event.location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Cria evento do Google Calendar a partir de dados de consulta
 */
export function createConsultationEvent(
  pacienteNome: string,
  tipo: 'primeira' | 'retorno',
  dataIso: string,
  notes?: string
): ConsultationEvent {
  const startDate = new Date(dataIso);
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 1); // Consulta dura 1 hora por padrão

  const title = `Consulta ${tipo === 'primeira' ? 'Inicial' : 'Retorno'} - ${pacienteNome}`;
  
  let description = `Consulta nutricional com ${pacienteNome}\n`;
  description += `Tipo: ${tipo === 'primeira' ? 'Primeira Consulta' : 'Retorno'}\n`;
  if (notes) {
    description += `\nObservações: ${notes}`;
  }

  return {
    title,
    description,
    startDate,
    endDate,
  };
}

/**
 * Abre o Google Calendar em nova aba com o evento preenchido (método manual)
 */
export function openGoogleCalendar(event: ConsultationEvent): void {
  const url = generateGoogleCalendarUrl(event);
  window.open(url, '_blank');
}

/**
 * Cria evento no Google Calendar via API
 */
export async function createGoogleCalendarEvent(
  userId: string,
  event: ConsultationEvent
): Promise<GoogleCalendarEvent> {
  const token = await getToken(userId);
  if (!token) {
    throw new Error('Usuário não autenticado com Google Calendar');
  }

  const googleEvent: GoogleCalendarEvent = {
    summary: event.title,
    description: event.description,
    start: {
      dateTime: event.startDate.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    end: {
      dateTime: event.endDate.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    location: event.location,
    reminders: {
      useDefault: true,
    },
  };

  const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(googleEvent),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Erro ao criar evento no Google Calendar');
  }

  return await response.json();
}

/**
 * Atualiza evento no Google Calendar via API
 */
export async function updateGoogleCalendarEvent(
  userId: string,
  eventId: string,
  event: ConsultationEvent
): Promise<GoogleCalendarEvent> {
  const token = await getToken(userId);
  if (!token) {
    throw new Error('Usuário não autenticado com Google Calendar');
  }

  const googleEvent: GoogleCalendarEvent = {
    summary: event.title,
    description: event.description,
    start: {
      dateTime: event.startDate.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    end: {
      dateTime: event.endDate.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    location: event.location,
    reminders: {
      useDefault: true,
    },
  };

  const response = await fetch(
    `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(googleEvent),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Erro ao atualizar evento no Google Calendar');
  }

  return await response.json();
}

/**
 * Deleta evento no Google Calendar via API
 */
export async function deleteGoogleCalendarEvent(
  userId: string,
  eventId: string
): Promise<void> {
  const token = await getToken(userId);
  if (!token) {
    throw new Error('Usuário não autenticado com Google Calendar');
  }

  const response = await fetch(
    `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token.access_token}`,
      },
    }
  );

  if (!response.ok && response.status !== 404) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Erro ao deletar evento no Google Calendar');
  }
}

/**
 * Lista eventos do Google Calendar
 */
export async function listGoogleCalendarEvents(
  userId: string,
  timeMin?: Date,
  timeMax?: Date,
  maxResults: number = 50
): Promise<GoogleCalendarEvent[]> {
  const token = await getToken(userId);
  if (!token) {
    throw new Error('Usuário não autenticado com Google Calendar');
  }

  const params = new URLSearchParams({
    maxResults: maxResults.toString(),
    singleEvents: 'true',
    orderBy: 'startTime',
  });

  if (timeMin) {
    params.append('timeMin', timeMin.toISOString());
  } else {
    params.append('timeMin', new Date().toISOString());
  }

  if (timeMax) {
    params.append('timeMax', timeMax.toISOString());
  }

  const response = await fetch(
    `https://www.googleapis.com/calendar/v3/calendars/primary/events?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token.access_token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Erro ao listar eventos do Google Calendar');
  }

  const data = await response.json();
  return data.items || [];
}

/**
 * Sincroniza consultas com Google Calendar
 * Cria eventos para consultas que ainda não têm googleEventId
 */
export async function syncConsultationsToGoogleCalendar(
  userId: string,
  consultations: Array<{
    id: string;
    pacienteNome: string;
    tipo: 'primeira' | 'retorno';
    dataIso: string;
    notes?: string;
    googleEventId?: string;
  }>
): Promise<Array<{ id: string; googleEventId: string }>> {
  const synced: Array<{ id: string; googleEventId: string }> = [];

  for (const consultation of consultations) {
    if (consultation.googleEventId) {
      continue; // Já sincronizado
    }

    try {
      const event = createConsultationEvent(
        consultation.pacienteNome,
        consultation.tipo,
        consultation.dataIso,
        consultation.notes
      );

      const googleEvent = await createGoogleCalendarEvent(userId, event);
      if (googleEvent.id) {
        synced.push({
          id: consultation.id,
          googleEventId: googleEvent.id,
        });
      }
    } catch (error) {
      console.error(`Erro ao sincronizar consulta ${consultation.id}:`, error);
    }
  }

  return synced;
}

